import { FormBlock, FormTheme } from './types'
import { COUNTRY_CODES } from './countryPhoneCodes'

// ── Phone Dropdown Script ──
function generatePhoneScript(): string {
  return `
(function(){
  var dropdowns = document.querySelectorAll('.fb-phone-dropdown');
  if (!dropdowns.length) return;
  dropdowns.forEach(function(dd) {
    var toggle = dd.querySelector('.fb-phone-toggle');
    var menu = dd.querySelector('.fb-phone-menu');
    var search = dd.querySelector('.fb-phone-search');
    var list = dd.querySelector('.fb-phone-list');
    var options = dd.querySelectorAll('.fb-phone-option');
    var flagEl = dd.querySelector('.fb-phone-flag');
    var dialEl = dd.querySelector('.fb-phone-dial');
    var wrapper = dd.closest('.fb-phone-wrapper');
    var phoneInput = wrapper ? wrapper.querySelector('.fb-phone-input') : null;
    var hiddenInput = wrapper ? wrapper.querySelector('input[type="hidden"]') : null;
    if (!toggle || !menu) return;

    function updateHidden() {
      if (hiddenInput && dialEl && phoneInput) {
        hiddenInput.value = dialEl.textContent + (phoneInput.value ? ' ' + phoneInput.value : '');
      }
    }

    toggle.addEventListener('click', function(e) {
      e.preventDefault();
      e.stopPropagation();
      var isOpen = menu.classList.contains('open');
      // Close all other open menus first
      document.querySelectorAll('.fb-phone-menu.open').forEach(function(m) { m.classList.remove('open'); });
      if (!isOpen) {
        menu.classList.add('open');
        if (search) { search.value = ''; search.focus(); }
        // Reset filter
        options.forEach(function(o) { o.style.display = ''; });
      }
    });

    if (search) {
      search.addEventListener('input', function() {
        var q = search.value.toLowerCase();
        options.forEach(function(o) {
          var name = (o.getAttribute('data-name') || '').toLowerCase();
          var dial = (o.getAttribute('data-dial') || '').toLowerCase();
          o.style.display = (name.indexOf(q) !== -1 || dial.indexOf(q) !== -1) ? '' : 'none';
        });
      });
      // Prevent menu close when typing in search
      search.addEventListener('click', function(e) { e.stopPropagation(); });
    }

    options.forEach(function(opt) {
      opt.addEventListener('click', function(e) {
        e.stopPropagation();
        var iso = opt.getAttribute('data-iso');
        var dial = opt.getAttribute('data-dial');
        var flag = opt.querySelector('.fb-phone-opt-flag');
        if (flagEl && flag) flagEl.textContent = flag.textContent;
        if (dialEl && dial) dialEl.textContent = dial;
        menu.classList.remove('open');
        updateHidden();
        if (phoneInput) phoneInput.focus();
      });
    });

    if (phoneInput) {
      phoneInput.addEventListener('input', updateHidden);
    }
  });

  // Close all menus when clicking outside
  document.addEventListener('click', function(e) {
    if (!e.target.closest('.fb-phone-dropdown')) {
      document.querySelectorAll('.fb-phone-menu.open').forEach(function(m) { m.classList.remove('open'); });
    }
  });
})();`
}

function hasPhoneBlock(blocks: FormBlock[]): boolean {
  for (const b of blocks) {
    if (b.type === 'phone') return true
    if (b.children) {
      if (hasPhoneBlock(b.children)) return true
    }
  }
  return false
}

export function generateFormHtml(
  blocks: FormBlock[],
  theme: FormTheme = DEFAULT_FORM_THEME,
): { html: string; css: string } {
  if (theme.layoutMode === 'card') {
    return generateCardFormHtml(blocks, theme)
  }

  const lines: string[] = []
  let hasPhone = false
  let hasRecaptcha = false

  // 1. Wrap in a container
  lines.push(
    '<div class="fb-form-container" style="max-width: 720px; margin: 0 auto; padding: 32px;">',
  )

  // 2. Add form wrapper
  lines.push('<form class="fb-form" novalidate>')

  let inHalfRow = false

  for (let i = 0; i < blocks.length; i++) {
    const block = blocks[i]
    if (!block) continue
    const nextBlock = blocks[i + 1]
    const isHalf = block.width === 'half'
    const nextIsHalf = nextBlock?.width === 'half'

    if (isHalf && !inHalfRow) {
      lines.push('  <div class="fb-row">')
      inHalfRow = true
    }

    switch (block.type) {
      case 'phone':
        hasPhone = true
        lines.push(renderPhoneField(block, theme))
        break
      case 'recaptcha':
        hasRecaptcha = true
        lines.push(
          `<div class="form-group" style="margin-bottom: ${theme.fieldSpacing}px;">`,
        )
        if (block.label && !theme.labelColor.includes('transparent')) {
          lines.push(
            `  <label style="display: block; margin-bottom: 6px; font-size: ${theme.labelFontSize}px; font-weight: ${theme.labelFontWeight}; color: ${theme.labelColor};">${block.label}</label>`,
          )
        }
        lines.push(
          `  <div class="g-recaptcha" data-sitekey="${block.recaptchaSiteKey || 'YOUR_SITE_KEY'}" data-theme="${block.recaptchaTheme || 'light'}" data-size="${block.recaptchaSize || 'normal'}"></div>`,
        )
        lines.push(`</div>`)
        break
      case 'submit':
        lines.push(renderSubmitButton(block, theme))
        break
      default:
        lines.push(renderBlock(block))
    }

    if (inHalfRow && (!nextIsHalf || !nextBlock)) {
      lines.push('  </div>')
      inHalfRow = false
    }
  }

  lines.push('</form>')
  lines.push('</div>')

  if (hasPhone) {
    lines.push(
      '<link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.8/css/intlTelInput.css"/>',
    )
    lines.push(
      '<script src="https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.8/js/intlTelInput.min.js"></script>',
    )
  }

  if (hasRecaptcha) {
    lines.push(
      '<script src="https://www.google.com/recaptcha/api.js" async defer></script>',
    )
  }

  // Add script for multi-step / conditional logic / phone init
  lines.push('<script>')
  lines.push(`(function(){
  var current = 0;
  var total = ${blocks.length};
  var steps = document.querySelectorAll('.fb-step');
  var bar = document.getElementById('fbProgressBar');
  var counter = document.getElementById('fbStepCounter');
  function show(idx) {
    steps.forEach(function(s, i) {
      s.classList.toggle('fb-step-active', i === idx);
    });
    if (bar) bar.style.width = ((idx + 1) / total * 100) + '%';
    if (counter) counter.textContent = 'Step ' + (idx + 1) + ' of ' + total;
    var active = steps[idx];
    if (active) {
      var inp = active.querySelector('input, select, textarea');
      if (inp) setTimeout(function(){ inp.focus(); }, 150);
    }
  }
  window.fbNext = function() {
    var active = steps[current];
    var required = active.querySelectorAll('[required]');
    for (var i = 0; i < required.length; i++) {
      if (!required[i].value || !required[i].value.trim()) {
        required[i].classList.add('fb-input-error');
        required[i].focus();
        return;
      }
      required[i].classList.remove('fb-input-error');
    }
    if (current < total - 1) { current++; show(current); }
  };
  window.fbPrev = function() {
    if (current > 0) { current--; show(current); }
  };
  document.addEventListener('keydown', function(e) {
    if (e.key === 'Enter') {
      var tag = e.target.tagName;
      if (tag === 'TEXTAREA') return;
      if (tag === 'BUTTON') return;
      e.preventDefault();
      if (current < total - 1) fbNext();
    }
  });
})();`)
  lines.push('</' + 'script>')

  // Add phone dropdown script if any phone blocks exist
  if (hasPhoneBlock(blocks)) {
    lines.push('<' + 'script>')
    lines.push(generatePhoneScript())
    lines.push('</' + 'script>')
  }

  const html = lines.join('\n')
  const css = generateFormCss(theme)

  return { html, css }
}

// ── Card Mode Generator ──

interface CardStep {
  decorativeBlocks: FormBlock[] // headings, paragraphs, dividers, spacers, images before the input
  inputBlock: FormBlock | null // the actual input field
}

function generateCardFormHtml(
  blocks: FormBlock[],
  theme: FormTheme,
): { html: string; css: string } {
  // Group blocks into steps
  const steps: CardStep[] = []
  const hiddenBlocks: FormBlock[] = []
  let pendingDecorative: FormBlock[] = []

  const inputTypes = new Set([
    'text',
    'email',
    'phone',
    'number',
    'textarea',
    'url',
    'select',
    'radio',
    'checkbox',
    'consent',
    'date',
    'file',
    'subform',
  ])

  for (const block of blocks) {
    if (!block) continue
    if (block.type === 'hidden') {
      hiddenBlocks.push(block)
      continue
    }
    if (block.type === 'submit') {
      // Submit goes on the last step — attach to pending or last step
      if (steps.length > 0) {
        // We'll handle submit separately
      }
      continue
    }
    if (inputTypes.has(block.type) || block.type === 'section') {
      if (block.type === 'section') {
        // Section title becomes decorative for the next input
        pendingDecorative.push(block)
        continue
      }
      steps.push({
        decorativeBlocks: [...pendingDecorative],
        inputBlock: block,
      })
      pendingDecorative = []
    } else {
      // Decorative block (heading, paragraph, divider, spacer, image)
      pendingDecorative.push(block)
    }
  }

  // If there are trailing decorative blocks with no input, make them a step
  if (pendingDecorative.length > 0) {
    steps.push({ decorativeBlocks: pendingDecorative, inputBlock: null })
  }

  // Find submit block
  const submitBlock = blocks.find((b) => b.type === 'submit')

  if (steps.length === 0) {
    return {
      html: '<form class="fb-form" novalidate></form>',
      css: generateFormCss(theme),
    }
  }

  const totalSteps = steps.length
  const lines: string[] = []

  const showProgress = theme.cardShowProgress !== false
  const showCounter = theme.cardShowCounter !== false

  lines.push('<div class="fb-card-wrapper">')
  if (showProgress) {
    lines.push(
      '  <div class="fb-card-progress"><div class="fb-card-progress-bar" id="fbProgressBar"></div></div>',
    )
  }
  if (showCounter) {
    lines.push(
      '  <div class="fb-card-counter" id="fbStepCounter">Step 1 of ' +
        totalSteps +
        '</div>',
    )
  }
  lines.push('  <form class="fb-form fb-card-mode" novalidate>')

  // Hidden fields
  for (const hb of hiddenBlocks) {
    lines.push('    ' + renderBlock(hb))
  }

  steps.forEach((step, idx) => {
    const isFirst = idx === 0
    const isLast = idx === totalSteps - 1
    const activeClass = isFirst ? ' fb-step-active' : ''

    lines.push(`    <div class="fb-step${activeClass}" data-step="${idx}">`)
    lines.push(`      <div class="fb-step-content">`)

    // Render decorative blocks
    for (const db of step.decorativeBlocks) {
      if (db.type === 'section') {
        lines.push(
          `        <h3 class="fb-heading fb-section-title">${esc(db.sectionTitle || db.label || 'Section')}</h3>`,
        )
      } else {
        lines.push('        ' + renderBlock(db))
      }
    }

    // Render input block
    if (step.inputBlock) {
      lines.push('        ' + renderBlock(step.inputBlock))
    }

    // Render submit on last step
    if (isLast && submitBlock) {
      lines.push('        ' + renderBlock(submitBlock))
    }

    lines.push(`      </div>`)

    // Navigation
    const navStyle = theme.cardNavStyle || 'text'
    const prevLabel =
      navStyle === 'icons' ? '&#8249;' : esc(theme.cardPrevLabel || '← Back')
    const nextLabel =
      navStyle === 'icons' ? '&#8250;' : esc(theme.cardNextLabel || 'Next →')
    const navClass =
      navStyle === 'pills'
        ? ' fb-card-nav-pills'
        : navStyle === 'icons'
          ? ' fb-card-nav-icons'
          : ''

    lines.push(`      <div class="fb-card-nav${navClass}">`)
    if (!isFirst) {
      lines.push(
        `        <button type="button" class="fb-nav-btn fb-nav-prev" onclick="fbPrev()">${prevLabel}</button>`,
      )
    } else {
      lines.push(`        <div></div>`)
    }
    if (!isLast) {
      lines.push(
        `        <button type="button" class="fb-nav-btn fb-nav-next" onclick="fbNext()">${nextLabel}</button>`,
      )
    }
    lines.push(`      </div>`)

    lines.push(`    </div>`)
  })

  lines.push('  </form>')
  lines.push('</div>')

  // Inline JS for navigation
  lines.push('<' + 'script>')
  lines.push(`(function(){
  var current = 0;
  var total = ${totalSteps};
  var steps = document.querySelectorAll('.fb-step');
  var bar = document.getElementById('fbProgressBar');
  var counter = document.getElementById('fbStepCounter');
  function show(idx) {
    steps.forEach(function(s, i) {
      s.classList.toggle('fb-step-active', i === idx);
    });
    if (bar) bar.style.width = ((idx + 1) / total * 100) + '%';
    if (counter) counter.textContent = 'Step ' + (idx + 1) + ' of ' + total;
    var active = steps[idx];
    if (active) {
      var inp = active.querySelector('input, select, textarea');
      if (inp) setTimeout(function(){ inp.focus(); }, 150);
    }
  }
  window.fbNext = function() {
    var active = steps[current];
    var required = active.querySelectorAll('[required]');
    for (var i = 0; i < required.length; i++) {
      if (!required[i].value || !required[i].value.trim()) {
        required[i].classList.add('fb-input-error');
        required[i].focus();
        return;
      }
      required[i].classList.remove('fb-input-error');
    }
    if (current < total - 1) { current++; show(current); }
  };
  window.fbPrev = function() {
    if (current > 0) { current--; show(current); }
  };
  document.addEventListener('keydown', function(e) {
    if (e.key === 'Enter') {
      var tag = e.target.tagName;
      if (tag === 'TEXTAREA') return;
      if (tag === 'BUTTON') return;
      e.preventDefault();
      if (current < total - 1) fbNext();
    }
  });
})();`)
  lines.push('</' + 'script>')

  // Add phone dropdown script if any phone blocks exist
  if (hasPhoneBlock(blocks)) {
    lines.push('<' + 'script>')
    lines.push(generatePhoneScript())
    lines.push('</' + 'script>')
  }

  const html = lines.join('\n')
  const css = generateFormCss(theme)

  return { html, css }
}

// Remove the entire duplicate generateCardFormHtml block (lines 695-921)

// Implement renderBlock and auxiliary functions:

function esc(str: string | undefined): string {
  if (!str) return ''
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function renderLabel(
  block: FormBlock,
  indent: string,
  theme?: FormTheme,
): string {
  if (!block.label) return ''
  var labelStyle = 'display: block; margin-bottom: 6px;'
  if (theme) {
    labelStyle += ' font-size: ' + theme.labelFontSize + 'px;'
    labelStyle += ' font-weight: ' + theme.labelFontWeight + ';'
    labelStyle += ' color: ' + theme.labelColor + ';'
  }
  var req = block.required ? '<span class="fb-required">*</span>' : ''
  return (
    indent +
    '<label class="fb-label" style="' +
    labelStyle +
    '">' +
    esc(block.label) +
    req +
    '</label>'
  )
}

function renderHelpText(block: FormBlock, indent: string): string {
  if (!block.helpText) return ''
  return indent + '<span class="fb-help">' + esc(block.helpText) + '</span>'
}

function renderInput(
  block: FormBlock,
  indent: string,
  wrapClass: string,
  wrapperAttr: string,
): string {
  var lines: string[] = []
  lines.push(indent + '<div class="' + wrapClass + '" ' + wrapperAttr + '>')
  if (block.label) {
    var req = block.required ? '<span class="fb-required">*</span>' : ''
    lines.push(
      indent +
        '  <label class="fb-label">' +
        esc(block.label) +
        req +
        '</label>',
    )
  }
  var attrs =
    'type="' +
    (block.type === 'url'
      ? 'url'
      : block.type === 'date'
        ? 'date'
        : block.type === 'number'
          ? 'number'
          : block.type === 'email'
            ? 'email'
            : 'text') +
    '"'
  attrs += ' name="' + esc(block.name) + '"'
  attrs += ' id="' + esc(block.name) + '"'
  if (block.placeholder)
    attrs += ' placeholder="' + esc(block.placeholder) + '"'
  if (block.required) attrs += ' required'
  if (block.defaultValue) attrs += ' value="' + esc(block.defaultValue) + '"'
  if (block.validation) {
    if (block.validation.minLength)
      attrs += ' minlength="' + block.validation.minLength + '"'
    if (block.validation.maxLength)
      attrs += ' maxlength="' + block.validation.maxLength + '"'
    if (block.validation.min != null)
      attrs += ' min="' + block.validation.min + '"'
    if (block.validation.max != null)
      attrs += ' max="' + block.validation.max + '"'
    if (block.validation.pattern)
      attrs += ' pattern="' + esc(block.validation.pattern) + '"'
  }
  lines.push(indent + '  <input ' + attrs + ' class="fb-input" />')
  if (block.helpText)
    lines.push(
      indent + '  <span class="fb-help">' + esc(block.helpText) + '</span>',
    )
  lines.push(indent + '</div>')
  return lines.join('\n')
}

function renderTextarea(
  block: FormBlock,
  indent: string,
  wrapClass: string,
  wrapperAttr: string,
): string {
  var lines: string[] = []
  lines.push(indent + '<div class="' + wrapClass + '" ' + wrapperAttr + '>')
  if (block.label) {
    var req = block.required ? '<span class="fb-required">*</span>' : ''
    lines.push(
      indent +
        '  <label class="fb-label">' +
        esc(block.label) +
        req +
        '</label>',
    )
  }
  var attrs =
    'name="' + esc(block.name) + '" id="' + esc(block.name) + '" rows="4"'
  if (block.placeholder)
    attrs += ' placeholder="' + esc(block.placeholder) + '"'
  if (block.required) attrs += ' required'
  if (block.validation) {
    if (block.validation.minLength)
      attrs += ' minlength="' + block.validation.minLength + '"'
    if (block.validation.maxLength)
      attrs += ' maxlength="' + block.validation.maxLength + '"'
  }
  lines.push(
    indent +
      '  <textarea ' +
      attrs +
      ' class="fb-input fb-textarea">' +
      esc(block.defaultValue || '') +
      '</textarea>',
  )
  if (block.helpText)
    lines.push(
      indent + '  <span class="fb-help">' + esc(block.helpText) + '</span>',
    )
  lines.push(indent + '</div>')
  return lines.join('\n')
}

function renderSelect(
  block: FormBlock,
  indent: string,
  wrapClass: string,
  wrapperAttr: string,
): string {
  var lines: string[] = []
  lines.push(indent + '<div class="' + wrapClass + '" ' + wrapperAttr + '>')
  if (block.label) {
    var req = block.required ? '<span class="fb-required">*</span>' : ''
    lines.push(
      indent +
        '  <label class="fb-label">' +
        esc(block.label) +
        req +
        '</label>',
    )
  }
  var attrs = 'name="' + esc(block.name) + '" id="' + esc(block.name) + '"'
  if (block.required) attrs += ' required'
  lines.push(indent + '  <select ' + attrs + ' class="fb-input fb-select">')
  lines.push(
    indent +
      '    <option value="">' +
      esc(block.placeholder || 'Select...') +
      '</option>',
  )
  var options = block.validation?.options || []
  for (var i = 0; i < options.length; i++) {
    var opt = options[i]
    var selected = block.defaultValue === opt.value ? ' selected' : ''
    lines.push(
      indent +
        '    <option value="' +
        esc(opt.value) +
        '"' +
        selected +
        '>' +
        esc(opt.label) +
        '</option>',
    )
  }
  lines.push(indent + '  </select>')
  if (block.helpText)
    lines.push(
      indent + '  <span class="fb-help">' + esc(block.helpText) + '</span>',
    )
  lines.push(indent + '</div>')
  return lines.join('\n')
}

function renderRadioGroup(
  block: FormBlock,
  indent: string,
  wrapClass: string,
  wrapperAttr: string,
): string {
  var lines: string[] = []
  lines.push(indent + '<div class="' + wrapClass + '" ' + wrapperAttr + '>')
  if (block.label) {
    var req = block.required ? '<span class="fb-required">*</span>' : ''
    lines.push(
      indent +
        '  <label class="fb-label">' +
        esc(block.label) +
        req +
        '</label>',
    )
  }
  var layout = block.optionsLayout || 'vertical'
  var cols = block.optionsColumns || 1
  var layoutClass = layout === 'horizontal' ? ' fb-options-horizontal' : ''
  if (cols > 1) layoutClass += ' fb-options-cols-' + cols
  lines.push(indent + '  <div class="fb-options' + layoutClass + '">')
  var options = block.validation?.options || []
  for (var i = 0; i < options.length; i++) {
    var opt = options[i]
    var checked = block.defaultValue === opt.value ? ' checked' : ''
    var reqAttr = block.required ? ' required' : ''
    lines.push(
      indent +
        '    <label class="fb-option"><input type="radio" name="' +
        esc(block.name) +
        '" value="' +
        esc(opt.value) +
        '"' +
        checked +
        reqAttr +
        ' /><span>' +
        esc(opt.label) +
        '</span></label>',
    )
  }
  lines.push(indent + '  </div>')
  if (block.helpText)
    lines.push(
      indent + '  <span class="fb-help">' + esc(block.helpText) + '</span>',
    )
  lines.push(indent + '</div>')
  return lines.join('\n')
}

function renderCheckboxGroup(
  block: FormBlock,
  indent: string,
  wrapClass: string,
  wrapperAttr: string,
): string {
  var lines: string[] = []
  lines.push(indent + '<div class="' + wrapClass + '" ' + wrapperAttr + '>')
  if (block.label) {
    var req = block.required ? '<span class="fb-required">*</span>' : ''
    lines.push(
      indent +
        '  <label class="fb-label">' +
        esc(block.label) +
        req +
        '</label>',
    )
  }
  var layout = block.optionsLayout || 'vertical'
  var cols = block.optionsColumns || 1
  var layoutClass = layout === 'horizontal' ? ' fb-options-horizontal' : ''
  if (cols > 1) layoutClass += ' fb-options-cols-' + cols
  lines.push(indent + '  <div class="fb-options' + layoutClass + '">')
  var options = block.validation?.options || []
  for (var i = 0; i < options.length; i++) {
    var opt = options[i]
    lines.push(
      indent +
        '    <label class="fb-option"><input type="checkbox" name="' +
        esc(block.name) +
        '" value="' +
        esc(opt.value) +
        '" /><span>' +
        esc(opt.label) +
        '</span></label>',
    )
  }
  lines.push(indent + '  </div>')
  if (block.helpText)
    lines.push(
      indent + '  <span class="fb-help">' + esc(block.helpText) + '</span>',
    )
  lines.push(indent + '</div>')
  return lines.join('\n')
}

function renderConsent(
  block: FormBlock,
  indent: string,
  wrapClass: string,
  wrapperAttr: string,
): string {
  var lines: string[] = []
  lines.push(
    indent + '<div class="' + wrapClass + ' fb-consent" ' + wrapperAttr + '>',
  )
  var reqAttr = block.required ? ' required' : ''
  lines.push(
    indent +
      '  <label class="fb-option fb-consent-label"><input type="checkbox" name="' +
      esc(block.name) +
      '" value="yes"' +
      reqAttr +
      ' /><span>' +
      (block.consentText || block.label || 'I agree') +
      '</span></label>',
  )
  if (block.helpText)
    lines.push(
      indent + '  <span class="fb-help">' + esc(block.helpText) + '</span>',
    )
  lines.push(indent + '</div>')
  return lines.join('\n')
}

function renderFileInput(
  block: FormBlock,
  indent: string,
  wrapClass: string,
  wrapperAttr: string,
): string {
  var lines: string[] = []
  lines.push(indent + '<div class="' + wrapClass + '" ' + wrapperAttr + '>')
  if (block.label) {
    var req = block.required ? '<span class="fb-required">*</span>' : ''
    lines.push(
      indent +
        '  <label class="fb-label">' +
        esc(block.label) +
        req +
        '</label>',
    )
  }
  var attrs =
    'type="file" name="' + esc(block.name) + '" id="' + esc(block.name) + '"'
  if (block.required) attrs += ' required'
  if (block.validation?.accept)
    attrs += ' accept="' + esc(block.validation.accept) + '"'
  lines.push(indent + '  <input ' + attrs + ' class="fb-input fb-file" />')
  if (block.helpText)
    lines.push(
      indent + '  <span class="fb-help">' + esc(block.helpText) + '</span>',
    )
  lines.push(indent + '</div>')
  return lines.join('\n')
}

function renderPhoneInput(
  block: FormBlock,
  indent: string,
  wrapClass: string,
  wrapperAttr: string,
): string {
  var defaultCountry = block.phoneDefaultCountry || 'US'
  var defaultEntry =
    COUNTRY_CODES.find(function (c) {
      return c.iso === defaultCountry
    }) || COUNTRY_CODES[0]
  var lines: string[] = []
  lines.push(indent + '<div class="' + wrapClass + '" ' + wrapperAttr + '>')
  if (block.label) {
    var req = block.required ? '<span class="fb-required">*</span>' : ''
    lines.push(
      indent +
        '  <label class="fb-label">' +
        esc(block.label) +
        req +
        '</label>',
    )
  }
  lines.push(indent + '  <div class="fb-phone-wrapper">')
  lines.push(
    indent +
      '    <input type="hidden" name="' +
      esc(block.name) +
      '" value="' +
      esc(defaultEntry.dial) +
      '" />',
  )
  lines.push(indent + '    <div class="fb-phone-dropdown">')
  lines.push(indent + '      <button type="button" class="fb-phone-toggle">')
  lines.push(
    indent +
      '        <span class="fb-phone-flag">' +
      defaultEntry.flag +
      '</span>',
  )
  lines.push(
    indent +
      '        <span class="fb-phone-dial">' +
      esc(defaultEntry.dial) +
      '</span>',
  )
  lines.push(indent + '        <span class="fb-phone-arrow">▾</span>')
  lines.push(indent + '      </button>')
  lines.push(indent + '      <div class="fb-phone-menu">')
  lines.push(
    indent +
      '        <input type="text" class="fb-phone-search" placeholder="Search..." />',
  )
  lines.push(indent + '        <div class="fb-phone-list">')
  for (var i = 0; i < COUNTRY_CODES.length; i++) {
    var c = COUNTRY_CODES[i]
    lines.push(
      indent +
        '          <div class="fb-phone-option" data-iso="' +
        c.iso +
        '" data-dial="' +
        c.dial +
        '" data-name="' +
        esc(c.name) +
        '"><span class="fb-phone-opt-flag">' +
        c.flag +
        '</span> ' +
        esc(c.name) +
        ' <span class="fb-phone-opt-dial">' +
        c.dial +
        '</span></div>',
    )
  }
  lines.push(indent + '        </div>')
  lines.push(indent + '      </div>')
  lines.push(indent + '    </div>')
  var reqAttr = block.required ? ' required' : ''
  lines.push(
    indent +
      '    <input type="tel" class="fb-input fb-phone-input" placeholder="' +
      esc(block.placeholder || 'Phone number') +
      '"' +
      reqAttr +
      ' />',
  )
  lines.push(indent + '  </div>')
  if (block.helpText)
    lines.push(
      indent + '  <span class="fb-help">' + esc(block.helpText) + '</span>',
    )
  lines.push(indent + '</div>')
  return lines.join('\n')
}

function renderPhoneField(block: FormBlock, theme: FormTheme): string {
  var indent = block.width === 'half' ? '    ' : '  '
  var wrapClass = block.width === 'half' ? 'fb-field fb-half' : 'fb-field'
  var wrapperAttr = 'data-block-id="' + block.id + '"'
  return renderPhoneInput(block, indent, wrapClass, wrapperAttr)
}

function renderSubmitButton(
  block: FormBlock,
  theme: FormTheme,
  indent: string = '  ',
): string {
  var wrapperAttr = 'data-block-id="' + block.id + '"'
  return renderBlockSubmitInline(block, indent, wrapperAttr)
}

function generateFormCss(theme: FormTheme): string {
  var bg =
    theme.backgroundType === 'gradient'
      ? 'background: linear-gradient(' +
        theme.gradientDirection +
        ', ' +
        theme.gradientFrom +
        ', ' +
        theme.gradientTo +
        ')'
      : 'background: ' + theme.backgroundColor
  var shadow =
    theme.containerShadow === 'sm'
      ? 'box-shadow: 0 1px 2px 0 rgba(0,0,0,0.05);'
      : theme.containerShadow === 'md'
        ? 'box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1);'
        : theme.containerShadow === 'lg'
          ? 'box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1);'
          : ''
  var border = theme.containerBorder
    ? 'border: 1px solid ' + theme.inputBorderColor + ';'
    : ''
  var inputShadow = theme.inputShadow
    ? 'box-shadow: 0 1px 2px 0 rgba(0,0,0,0.05);'
    : ''
  var btnShadow = theme.buttonShadow
    ? 'box-shadow: 0 1px 3px 0 rgba(0,0,0,0.1), 0 1px 2px -1px rgba(0,0,0,0.1);'
    : ''
  var btnBg =
    theme.buttonStyle === 'outline'
      ? 'background: transparent; color: ' +
        theme.primaryColor +
        '; border: 2px solid ' +
        theme.primaryColor +
        ';'
      : 'background: ' +
        theme.primaryColor +
        '; color: ' +
        theme.buttonTextColor +
        '; border: 2px solid ' +
        theme.primaryColor +
        ';'

  return (
    '.fb-form-container { ' +
    bg +
    '; font-family: ' +
    theme.fontFamily +
    '; padding: ' +
    theme.formPadding +
    'px; border-radius: ' +
    theme.borderRadius +
    'px; ' +
    shadow +
    ' ' +
    border +
    ' }\n' +
    '.fb-form { max-width: 100%; }\n' +
    '.fb-field { margin-bottom: ' +
    theme.fieldSpacing +
    'px; }\n' +
    '.fb-label { display: block; margin-bottom: 6px; font-size: ' +
    theme.labelFontSize +
    'px; font-weight: ' +
    theme.labelFontWeight +
    '; color: ' +
    theme.labelColor +
    '; }\n' +
    '.fb-required { color: #ef4444; margin-left: 2px; }\n' +
    '.fb-input { display: block; width: 100%; padding: 10px 12px; border: 1px solid ' +
    theme.inputBorderColor +
    '; border-radius: ' +
    theme.borderRadius +
    'px; font-size: 14px; font-family: inherit; background: ' +
    theme.inputBackground +
    '; color: #111827; transition: border-color 0.15s, box-shadow 0.15s; ' +
    inputShadow +
    ' box-sizing: border-box; }\n' +
    '.fb-input:focus { outline: none; border-color: ' +
    theme.inputFocusColor +
    '; box-shadow: 0 0 0 3px ' +
    theme.inputFocusColor +
    '20; }\n' +
    '.fb-input-error { border-color: #ef4444 !important; }\n' +
    '.fb-textarea { resize: vertical; min-height: 80px; }\n' +
    ".fb-select { appearance: none; background-image: url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M6 8L1 3h10z'/%3E%3C/svg%3E\"); background-repeat: no-repeat; background-position: right 12px center; padding-right: 36px; }\n" +
    '.fb-help { display: block; margin-top: 4px; font-size: 12px; color: #9ca3af; }\n' +
    '.fb-options { display: flex; flex-direction: column; gap: 8px; }\n' +
    '.fb-options-horizontal { flex-direction: row; flex-wrap: wrap; }\n' +
    '.fb-options-cols-2 { display: grid; grid-template-columns: repeat(2, 1fr); }\n' +
    '.fb-options-cols-3 { display: grid; grid-template-columns: repeat(3, 1fr); }\n' +
    '.fb-options-cols-4 { display: grid; grid-template-columns: repeat(4, 1fr); }\n' +
    '.fb-option { display: flex; align-items: center; gap: 8px; font-size: 14px; color: #374151; cursor: pointer; }\n' +
    '.fb-option input { accent-color: ' +
    theme.primaryColor +
    '; }\n' +
    '.fb-heading { color: ' +
    theme.headingColor +
    '; margin: 0 0 8px; }\n' +
    '.fb-paragraph { color: ' +
    theme.paragraphColor +
    '; margin: 0 0 8px; font-size: 14px; line-height: 1.6; }\n' +
    '.fb-divider { border: none; border-top: 1px solid ' +
    theme.inputBorderColor +
    '; margin: 16px 0; }\n' +
    '.fb-submit { margin-top: ' +
    theme.fieldSpacing +
    'px; }\n' +
    '.fb-submit-full .fb-button { width: 100%; }\n' +
    '.fb-submit-center { text-align: center; }\n' +
    '.fb-submit-right { text-align: right; }\n' +
    '.fb-button { ' +
    btnBg +
    ' padding: 12px 24px; border-radius: ' +
    theme.borderRadius +
    'px; font-size: 16px; font-weight: 600; font-family: inherit; cursor: pointer; transition: opacity 0.15s; ' +
    btnShadow +
    ' }\n' +
    '.fb-button:hover { opacity: 0.9; }\n' +
    '.fb-button:disabled { opacity: 0.6; cursor: not-allowed; }\n' +
    '.fb-row { display: flex; gap: 16px; }\n' +
    '.fb-half { flex: 1; min-width: 0; }\n' +
    '.fb-consent-label span { font-size: 13px; color: #6b7280; }\n' +
    '.fb-image-wrap { margin-bottom: ' +
    theme.fieldSpacing +
    'px; }\n' +
    '.fb-image { max-width: 100%; height: auto; }\n' +
    '.fb-video-wrap { margin-bottom: ' +
    theme.fieldSpacing +
    'px; }\n' +
    '.fb-section { border: 1px solid ' +
    theme.inputBorderColor +
    '; border-radius: ' +
    theme.borderRadius +
    'px; padding: 16px; margin-bottom: ' +
    theme.fieldSpacing +
    'px; }\n' +
    '.fb-section-noborder { border: none; padding: 0; }\n' +
    '.fb-section-dashed { border-style: dashed; }\n' +
    '.fb-section-accent { border-left: 3px solid ' +
    theme.primaryColor +
    '; }\n' +
    '.fb-section-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }\n' +
    '.fb-section-title { font-weight: 600; font-size: 15px; color: ' +
    theme.headingColor +
    '; }\n' +
    '.fb-section-collapsible { cursor: pointer; }\n' +
    '.fb-section-collapsed .fb-section-body { display: none; }\n' +
    '.fb-section-collapsed .fb-section-chevron { transform: rotate(-90deg); }\n' +
    '.fb-section-chevron { transition: transform 0.2s; }\n' +
    '.fb-columns { display: grid; grid-template-columns: repeat(var(--col-count, 2), 1fr); gap: var(--col-gap, 16px); margin-bottom: ' +
    theme.fieldSpacing +
    'px; }\n' +
    '.fb-subform { margin-bottom: ' +
    theme.fieldSpacing +
    'px; border: 1px solid ' +
    theme.inputBorderColor +
    '; border-radius: ' +
    theme.borderRadius +
    'px; padding: 16px; }\n' +
    '.fb-subform-header { margin-bottom: 12px; }\n' +
    '.fb-subform-title { font-weight: 600; font-size: 15px; color: ' +
    theme.headingColor +
    '; }\n' +
    '.fb-subform-table { width: 100%; border-collapse: collapse; }\n' +
    '.fb-sf-th { text-align: left; padding: 6px 8px; font-size: 12px; font-weight: 600; color: ' +
    theme.labelColor +
    '; border-bottom: 1px solid ' +
    theme.inputBorderColor +
    '; }\n' +
    '.fb-sf-td { padding: 6px 8px; vertical-align: top; }\n' +
    '.fb-sf-num { width: 30px; color: #9ca3af; font-size: 13px; }\n' +
    '.fb-sf-input { font-size: 13px; padding: 6px 8px; }\n' +
    '.fb-sf-remove { background: none; border: none; color: #ef4444; cursor: pointer; font-size: 14px; padding: 4px; }\n' +
    '.fb-sf-add { background: none; border: 1px dashed ' +
    theme.inputBorderColor +
    '; color: ' +
    theme.primaryColor +
    '; cursor: pointer; font-size: 13px; font-weight: 500; padding: 8px 16px; border-radius: ' +
    theme.borderRadius +
    'px; margin-top: 8px; width: 100%; }\n' +
    '.fb-sf-add:hover { background: ' +
    theme.primaryColor +
    '08; }\n' +
    '.fb-phone-wrapper { display: flex; gap: 0; }\n' +
    '.fb-phone-dropdown { position: relative; flex-shrink: 0; }\n' +
    '.fb-phone-toggle { display: flex; align-items: center; gap: 4px; padding: 10px 8px; border: 1px solid ' +
    theme.inputBorderColor +
    '; border-radius: ' +
    theme.borderRadius +
    'px 0 0 ' +
    theme.borderRadius +
    'px; background: ' +
    theme.inputBackground +
    '; cursor: pointer; font-size: 14px; height: 100%; box-sizing: border-box; }\n' +
    '.fb-phone-flag { font-size: 18px; }\n' +
    '.fb-phone-dial { font-size: 13px; color: #6b7280; }\n' +
    '.fb-phone-arrow { font-size: 10px; color: #9ca3af; }\n' +
    '.fb-phone-menu { display: none; position: absolute; top: 100%; left: 0; z-index: 100; background: #fff; border: 1px solid ' +
    theme.inputBorderColor +
    '; border-radius: ' +
    theme.borderRadius +
    'px; box-shadow: 0 4px 12px rgba(0,0,0,0.1); width: 280px; max-height: 300px; overflow: hidden; }\n' +
    '.fb-phone-menu.open { display: block; }\n' +
    '.fb-phone-search { width: 100%; padding: 8px 12px; border: none; border-bottom: 1px solid ' +
    theme.inputBorderColor +
    '; font-size: 13px; outline: none; box-sizing: border-box; }\n' +
    '.fb-phone-list { max-height: 250px; overflow-y: auto; }\n' +
    '.fb-phone-option { display: flex; align-items: center; gap: 8px; padding: 8px 12px; cursor: pointer; font-size: 13px; }\n' +
    '.fb-phone-option:hover { background: #f3f4f6; }\n' +
    '.fb-phone-opt-flag { font-size: 16px; }\n' +
    '.fb-phone-opt-dial { color: #9ca3af; margin-left: auto; }\n' +
    '.fb-phone-input { flex: 1; border-radius: 0 ' +
    theme.borderRadius +
    'px ' +
    theme.borderRadius +
    'px 0; border-left: none; }\n' +
    '.fb-file { padding: 8px; }\n' +
    // Card mode styles
    '.fb-card-wrapper { max-width: ' +
    (theme.cardWidth || 720) +
    'px; margin: 0 auto; padding: ' +
    theme.formPadding +
    'px; font-family: ' +
    theme.fontFamily +
    '; ' +
    bg +
    '; border-radius: ' +
    theme.borderRadius +
    'px; ' +
    shadow +
    ' ' +
    border +
    ' }\n' +
    '.fb-card-progress { height: 4px; background: #e5e7eb; border-radius: 2px; margin-bottom: 16px; overflow: hidden; }\n' +
    '.fb-card-progress-bar { height: 100%; background: ' +
    theme.primaryColor +
    '; border-radius: 2px; transition: width 0.3s; }\n' +
    '.fb-card-counter { text-align: center; font-size: 12px; color: #9ca3af; margin-bottom: 24px; }\n' +
    '.fb-step { display: none; }\n' +
    '.fb-step-active { display: block; }\n' +
    '.fb-step-content { min-height: 120px; }\n' +
    '.fb-card-nav { display: flex; justify-content: space-between; align-items: center; margin-top: 24px; padding-top: 16px; border-top: 1px solid #f3f4f6; }\n' +
    '.fb-nav-btn { background: none; border: none; color: ' +
    theme.primaryColor +
    '; font-size: 14px; font-weight: 500; cursor: pointer; padding: 8px 16px; border-radius: ' +
    theme.borderRadius +
    'px; font-family: inherit; }\n' +
    '.fb-nav-btn:hover { background: ' +
    theme.primaryColor +
    '10; }\n' +
    '.fb-card-nav-pills .fb-nav-btn { background: ' +
    theme.primaryColor +
    '10; border-radius: 20px; }\n' +
    '.fb-card-nav-icons .fb-nav-btn { font-size: 24px; padding: 4px 12px; }\n' +
    // Image animations
    '@keyframes fb-fadeIn { from { opacity: 0; } to { opacity: 1; } }\n' +
    '@keyframes fb-slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }\n' +
    '@keyframes fb-slideDown { from { opacity: 0; transform: translateY(-20px); } to { opacity: 1; transform: translateY(0); } }\n' +
    '@keyframes fb-zoomIn { from { opacity: 0; transform: scale(0.9); } to { opacity: 1; transform: scale(1); } }\n' +
    '@keyframes fb-blurIn { from { opacity: 0; filter: blur(10px); } to { opacity: 1; filter: blur(0); } }\n' +
    '.fb-img-fadeIn { animation: fb-fadeIn 0.6s ease; }\n' +
    '.fb-img-slideUp { animation: fb-slideUp 0.6s ease; }\n' +
    '.fb-img-slideDown { animation: fb-slideDown 0.6s ease; }\n' +
    '.fb-img-zoomIn { animation: fb-zoomIn 0.6s ease; }\n' +
    '.fb-img-blurIn { animation: fb-blurIn 0.6s ease; }\n' +
    // Section animations
    '@keyframes fb-secSlideLeft { from { opacity: 0; transform: translateX(-20px); } to { opacity: 1; transform: translateX(0); } }\n' +
    '@keyframes fb-secSlideRight { from { opacity: 0; transform: translateX(20px); } to { opacity: 1; transform: translateX(0); } }\n' +
    '.fb-sec-slideLeft { animation: fb-secSlideLeft 0.5s ease; }\n' +
    '.fb-sec-slideRight { animation: fb-secSlideRight 0.5s ease; }\n' +
    '.fb-sec-slideUp { animation: fb-slideUp 0.5s ease; }\n' +
    '.fb-sec-slideDown { animation: fb-slideDown 0.5s ease; }\n' +
    '.fb-sec-fadeIn { animation: fb-fadeIn 0.5s ease; }\n' +
    '.fb-sec-zoomIn { animation: fb-zoomIn 0.5s ease; }\n' +
    // Responsive
    '@media (max-width: 600px) { .fb-row { flex-direction: column; } .fb-half { flex: none; } .fb-columns { grid-template-columns: 1fr !important; } }\n'
  )
}

function renderBlock(block: FormBlock): string {
  var indent = block.width === 'half' ? '    ' : '  '
  var wrapClass = block.width === 'half' ? 'fb-field fb-half' : 'fb-field'
  var wrapperAttr = 'data-block-id="' + block.id + '"'

  switch (block.type) {
    case 'phone':
      return renderPhoneInput(block, indent, wrapClass, wrapperAttr)
    case 'text':
    case 'email':
    case 'number':
    case 'url':
    case 'date':
      return renderInput(block, indent, wrapClass, wrapperAttr)
    case 'textarea':
      return renderTextarea(block, indent, wrapClass, wrapperAttr)
    case 'select':
      return renderSelect(block, indent, wrapClass, wrapperAttr)
    case 'radio':
      return renderRadioGroup(block, indent, wrapClass, wrapperAttr)
    case 'checkbox':
      return renderCheckboxGroup(block, indent, wrapClass, wrapperAttr)
    case 'consent':
      return renderConsent(block, indent, wrapClass, wrapperAttr)
    case 'file':
      return renderFileInput(block, indent, wrapClass, wrapperAttr)
    case 'hidden':
      return (
        indent +
        '<input type="hidden" name="' +
        esc(block.name) +
        '" id="' +
        esc(block.name) +
        '" value="' +
        esc(block.defaultValue) +
        '" ' +
        wrapperAttr +
        ' />'
      )
    case 'heading':
      return renderBlockHeading(block, indent, wrapperAttr)
    case 'paragraph':
      return renderBlockParagraph(block, indent, wrapperAttr)
    case 'divider':
      return indent + '<hr class="fb-divider" ' + wrapperAttr + ' />'
    case 'spacer':
      return (
        indent +
        '<div class="fb-spacer" style="height:' +
        (block.height || 24) +
        'px" ' +
        wrapperAttr +
        '></div>'
      )
    case 'video_embed':
      return renderBlockVideoEmbed(block, indent, wrapperAttr)
    case 'image':
      return renderBlockImage(block, indent, wrapperAttr)
    case 'submit':
      return renderBlockSubmitInline(block, indent, wrapperAttr)
    case 'section':
      return renderBlockSection(block, indent, wrapperAttr)
    case 'columns':
      return renderBlockColumns(block, indent, wrapperAttr)
    case 'subform':
      return renderBlockSubform(block, indent, wrapperAttr)
    default:
      return ''
  }
}

function renderBlockHeading(
  block: FormBlock,
  indent: string,
  wrapperAttr: string,
): string {
  var tag = 'h' + (block.level || 2)
  var hStyle =
    block.textAlign && block.textAlign !== 'left'
      ? ' style="text-align:' + block.textAlign + '"'
      : ''
  return (
    indent +
    '<' +
    tag +
    ' class="fb-heading"' +
    hStyle +
    ' ' +
    wrapperAttr +
    '>' +
    esc(block.content || '') +
    '</' +
    tag +
    '>'
  )
}

function renderBlockParagraph(
  block: FormBlock,
  indent: string,
  wrapperAttr: string,
): string {
  var pStyle =
    block.textAlign && block.textAlign !== 'left'
      ? ' style="text-align:' + block.textAlign + '"'
      : ''
  return (
    indent +
    '<p class="fb-paragraph"' +
    pStyle +
    ' ' +
    wrapperAttr +
    '>' +
    esc(block.content || '') +
    '</p>'
  )
}

function renderBlockVideoEmbed(
  block: FormBlock,
  indent: string,
  wrapperAttr: string,
): string {
  if (!block.videoUrl) {
    return (
      indent +
      '<div class="fb-video-placeholder" style="background:#f3f4f6;border:2px dashed #d1d5db;border-radius:6px;padding:40px;text-align:center;color:#9ca3af;font-size:14px;" ' +
      wrapperAttr +
      '>Video / GIF placeholder</div>'
    )
  }
  var isMp4 = block.videoUrl.toLowerCase().endsWith('.mp4')
  var isGif = block.videoUrl.toLowerCase().endsWith('.gif')
  var isYoutube =
    block.videoUrl.includes('youtube.com') ||
    block.videoUrl.includes('youtu.be')
  var isVimeo = block.videoUrl.includes('vimeo.com')
  if (isGif) {
    return (
      indent +
      '<img src="' +
      esc(block.videoUrl) +
      '" alt="GIF" style="width:100%;height:auto;border-radius:6px;display:block;" ' +
      wrapperAttr +
      ' />'
    )
  }
  if (isMp4) {
    var attrs = [
      'controls',
      block.videoAutoplay ? 'autoplay' : '',
      block.videoMuted ? 'muted' : '',
      block.videoLoop ? 'loop' : '',
      'playsinline',
    ]
      .filter(Boolean)
      .join(' ')
    return (
      indent +
      '<video src="' +
      esc(block.videoUrl) +
      '" ' +
      attrs +
      ' style="width:100%;height:auto;border-radius:6px;display:block;" ' +
      wrapperAttr +
      '></video>'
    )
  }
  var embedUrl = block.videoUrl
  if (isYoutube) {
    var videoId = block.videoUrl.match(
      /(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^&?]+)/,
    )?.[1]
    if (videoId) {
      embedUrl =
        'https://www.youtube.com/embed/' +
        videoId +
        '?rel=0' +
        (block.videoAutoplay ? '&autoplay=1' : '') +
        (block.videoMuted ? '&mute=1' : '') +
        (block.videoLoop ? '&loop=1&playlist=' + videoId : '')
    }
  } else if (isVimeo) {
    var vimeoId = block.videoUrl.match(/vimeo\.com\/(?:video\/)?([0-9]+)/)?.[1]
    if (vimeoId) {
      embedUrl =
        'https://player.vimeo.com/video/' +
        vimeoId +
        '?title=0&byline=0&portrait=0' +
        (block.videoAutoplay ? '&autoplay=1' : '') +
        (block.videoMuted ? '&muted=1' : '') +
        (block.videoLoop ? '&loop=1' : '')
    }
  }
  var paddingBottom =
    block.videoAspectRatio === '4:3'
      ? '75%'
      : block.videoAspectRatio === '1:1'
        ? '100%'
        : block.videoAspectRatio === '9:16'
          ? '177.77%'
          : '56.25%'
  return (
    indent +
    '<div class="fb-video-wrap" style="position:relative;width:100%;padding-bottom:' +
    paddingBottom +
    ';background:#f3f4f6;border-radius:6px;overflow:hidden;" ' +
    wrapperAttr +
    '>\n' +
    indent +
    '  <iframe src="' +
    esc(embedUrl) +
    '" style="position:absolute;top:0;left:0;width:100%;height:100%;border:0;" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>\n' +
    indent +
    '</div>'
  )
}

function renderBlockImage(
  block: FormBlock,
  indent: string,
  wrapperAttr: string,
): string {
  if (!block.src)
    return (
      indent +
      '<div class="fb-image-placeholder" ' +
      wrapperAttr +
      '>Image</div>'
    )
  var imgStyles: string[] = ['display:block']
  var wrapStyles: string[] = []
  var sizing = block.imageSizing || 'maxWidth'
  if (sizing === 'maxWidth') {
    imgStyles.push('max-width:' + (block.imageMaxWidth || 100) + '%')
    imgStyles.push('height:auto')
  } else if (sizing === 'custom') {
    if (block.imageWidth) imgStyles.push('width:' + block.imageWidth + 'px')
    if (block.imageHeight) imgStyles.push('height:' + block.imageHeight + 'px')
    if (block.imageMaxWidth)
      imgStyles.push('max-width:' + block.imageMaxWidth + '%')
  }
  if (block.imageMaxHeight)
    imgStyles.push('max-height:' + block.imageMaxHeight + 'px')
  if (block.imageBorderRadius)
    imgStyles.push('border-radius:' + block.imageBorderRadius + 'px')
  if (block.imageOpacity != null && block.imageOpacity < 100)
    imgStyles.push('opacity:' + block.imageOpacity / 100)
  if (block.imageObjectFit) imgStyles.push('object-fit:' + block.imageObjectFit)
  if (block.imageShadow === 'sm')
    imgStyles.push('box-shadow:0 1px 2px 0 rgba(0,0,0,0.05)')
  else if (block.imageShadow === 'md')
    imgStyles.push('box-shadow:0 4px 6px -1px rgba(0,0,0,0.1)')
  else if (block.imageShadow === 'lg')
    imgStyles.push('box-shadow:0 10px 15px -3px rgba(0,0,0,0.1)')
  var animClass =
    block.imageAnimation && block.imageAnimation !== 'none'
      ? ' fb-img-' + block.imageAnimation
      : ''
  var imgAlign = block.imageAlign || 'center'
  wrapStyles.push('text-align:' + imgAlign)
  if (block.imagePosition && block.imagePosition !== 'static') {
    wrapStyles.push('position:' + block.imagePosition)
    if (block.imageTopOffset != null)
      wrapStyles.push('top:' + block.imageTopOffset + 'px')
    if (block.imageLeftOffset != null)
      wrapStyles.push('left:' + block.imageLeftOffset + 'px')
    if (block.imagePosition === 'sticky') wrapStyles.push('z-index:10')
    if (block.imagePosition === 'fixed') wrapStyles.push('z-index:1000')
    if (block.imagePosition === 'absolute') wrapStyles.push('z-index:10')
  }
  if (imgAlign === 'center') imgStyles.push('margin:0 auto')
  else if (imgAlign === 'right') imgStyles.push('margin-left:auto')
  var wrapStyleAttr = wrapStyles.length
    ? ' style="' + wrapStyles.join(';') + '"'
    : ''
  return (
    indent +
    '<div class="fb-image-wrap' +
    animClass +
    '"' +
    wrapStyleAttr +
    ' ' +
    wrapperAttr +
    '><img src="' +
    esc(block.src) +
    '" alt="' +
    esc(block.alt || '') +
    '" class="fb-image" style="' +
    imgStyles.join(';') +
    '" /></div>'
  )
}

function renderBlockSubmitInline(
  block: FormBlock,
  indent: string,
  wrapperAttr: string,
): string {
  var align = block.buttonAlign || 'full'
  var submitStyles: string[] = []
  var btnInlineStyles: string[] = []
  if (block.buttonColor) {
    btnInlineStyles.push('background:' + esc(block.buttonColor))
    btnInlineStyles.push('border-color:' + esc(block.buttonColor))
  }
  if (block.buttonPosition === 'sticky') {
    submitStyles.push('position:sticky')
    submitStyles.push('bottom:' + (block.buttonStickyOffset || 0) + 'px')
    submitStyles.push('z-index:50')
    submitStyles.push('background:inherit')
    submitStyles.push('padding:12px 0')
  } else if (block.buttonPosition === 'fixed') {
    submitStyles.push('position:fixed')
    submitStyles.push('bottom:' + (block.buttonStickyOffset || 0) + 'px')
    submitStyles.push('left:0')
    submitStyles.push('right:0')
    submitStyles.push('z-index:1000')
    submitStyles.push('padding:12px 24px')
    submitStyles.push('background:rgba(255,255,255,0.95)')
    submitStyles.push('backdrop-filter:blur(8px)')
    submitStyles.push('box-shadow:0 -2px 10px rgba(0,0,0,0.1)')
  }
  var submitStyleAttr = submitStyles.length
    ? ' style="' + submitStyles.join(';') + '"'
    : ''
  var btnStyleAttr = btnInlineStyles.length
    ? ' style="' + btnInlineStyles.join(';') + '"'
    : ''
  return (
    indent +
    '<div class="fb-submit fb-submit-' +
    align +
    '"' +
    submitStyleAttr +
    ' ' +
    wrapperAttr +
    '><button type="submit" class="fb-button"' +
    btnStyleAttr +
    '>' +
    esc(block.buttonText || 'Submit') +
    '</button></div>'
  )
}

function renderBlockSection(
  block: FormBlock,
  indent: string,
  wrapperAttr: string,
): string {
  var animClass =
    block.sectionAnimation && block.sectionAnimation !== 'none'
      ? ' fb-section-anim fb-sec-' + block.sectionAnimation
      : ''
  var borderClass =
    block.sectionBorderStyle === 'dashed'
      ? ' fb-section-dashed'
      : block.sectionBorderStyle === 'accent'
        ? ' fb-section-accent'
        : block.sectionBorderStyle === 'none'
          ? ' fb-section-noborder'
          : ''
  var bgStyle = block.sectionBackground
    ? ' style="background:' + esc(block.sectionBackground) + '"'
    : ''
  var collapsible = block.sectionCollapsible
  var showTitle = block.sectionShowTitle !== false
  var sectionLines: string[] = []
  sectionLines.push(
    indent +
      '<div class="fb-section' +
      animClass +
      borderClass +
      '"' +
      bgStyle +
      ' ' +
      wrapperAttr +
      '>',
  )
  if (showTitle && block.sectionTitle) {
    if (collapsible) {
      sectionLines.push(
        indent +
          '  <div class="fb-section-header fb-section-collapsible" onclick="this.parentElement.classList.toggle(\'fb-section-collapsed\')">',
      )
      sectionLines.push(
        indent +
          '    <span class="fb-section-title">' +
          esc(block.sectionTitle) +
          '</span>',
      )
      sectionLines.push(
        indent + '    <span class="fb-section-chevron">▾</span>',
      )
      sectionLines.push(indent + '  </div>')
    } else {
      sectionLines.push(indent + '  <div class="fb-section-header">')
      sectionLines.push(
        indent +
          '    <span class="fb-section-title">' +
          esc(block.sectionTitle) +
          '</span>',
      )
      sectionLines.push(indent + '  </div>')
    }
  }
  sectionLines.push(indent + '  <div class="fb-section-body">')
  if (block.children && block.children.length > 0) {
    for (var ci = 0; ci < block.children.length; ci++) {
      sectionLines.push(renderBlock(block.children[ci]))
    }
  }
  sectionLines.push(indent + '  </div>')
  sectionLines.push(indent + '</div>')
  return sectionLines.join('\n')
}

function renderBlockColumns(
  block: FormBlock,
  indent: string,
  wrapperAttr: string,
): string {
  var colCount = block.columnCount || 2
  var gap = block.columnGap || 16
  var cols = block.columns || []
  var colLines: string[] = []
  colLines.push(
    indent +
      '<div class="fb-columns" style="--col-count: ' +
      colCount +
      '; --col-gap: ' +
      gap +
      'px;" ' +
      wrapperAttr +
      '>',
  )
  for (var i = 0; i < colCount; i++) {
    var colBlocks = cols[i] || []
    colLines.push(indent + '  <div class="fb-col">')
    for (var j = 0; j < colBlocks.length; j++) {
      colLines.push(renderBlock(colBlocks[j]))
    }
    colLines.push(indent + '  </div>')
  }
  colLines.push(indent + '</div>')
  return colLines.join('\n')
}

function renderBlockSubform(
  block: FormBlock,
  indent: string,
  wrapperAttr: string,
): string {
  var sfCols = block.subformColumns || [
    { label: 'Column 1', type: 'text' },
    { label: 'Column 2', type: 'text' },
  ]
  var sfMin = block.subformMinRows || 1
  var sfMax = block.subformMaxRows || 10
  var sfShowName = block.subformShowName !== false
  var sfName = esc(block.subformName || block.name || 'subform')
  var sfLines: string[] = []
  sfLines.push(
    indent +
      '<div class="fb-subform" ' +
      wrapperAttr +
      ' data-sf-name="' +
      esc(block.name) +
      '" data-sf-min="' +
      sfMin +
      '" data-sf-max="' +
      sfMax +
      '">',
  )
  if (sfShowName) {
    sfLines.push(
      indent +
        '  <div class="fb-subform-header"><span class="fb-subform-title">' +
        sfName +
        '</span></div>',
    )
  }
  sfLines.push(indent + '  <table class="fb-subform-table">')
  sfLines.push(indent + '    <thead><tr>')
  sfLines.push(indent + '      <th class="fb-sf-th">#</th>')
  for (var ci = 0; ci < sfCols.length; ci++) {
    var col = sfCols[ci]
    sfLines.push(
      indent +
        '      <th class="fb-sf-th">' +
        esc(col.label) +
        (col.required ? '<span class="fb-required">*</span>' : '') +
        '</th>',
    )
  }
  sfLines.push(indent + '      <th class="fb-sf-th fb-sf-actions"></th>')
  sfLines.push(indent + '    </tr></thead>')
  sfLines.push(indent + '    <tbody class="fb-sf-body">')
  for (var r = 0; r < sfMin; r++) {
    sfLines.push(indent + '      <tr class="fb-sf-row">')
    sfLines.push(
      indent + '        <td class="fb-sf-td fb-sf-num">' + (r + 1) + '</td>',
    )
    for (var k = 0; k < sfCols.length; k++) {
      var sfCol = sfCols[k]
      var fieldName =
        esc(block.name) +
        '[' +
        r +
        '][' +
        esc(sfCol.label.toLowerCase().replace(/\s+/g, '_')) +
        ']'
      var reqAttr = sfCol.required ? ' required' : ''
      var phAttr = sfCol.placeholder
        ? ' placeholder="' + esc(sfCol.placeholder) + '"'
        : ''
      if (sfCol.type === 'select') {
        sfLines.push(
          indent +
            '        <td class="fb-sf-td"><select name="' +
            fieldName +
            '" class="fb-input fb-sf-input"' +
            reqAttr +
            '>',
        )
        sfLines.push(
          indent +
            '          <option value="">' +
            (sfCol.placeholder || 'Select...') +
            '</option>',
        )
        var sfOpts = sfCol.options || []
        for (var oi = 0; oi < sfOpts.length; oi++) {
          sfLines.push(
            indent +
              '          <option value="' +
              esc(sfOpts[oi].value) +
              '">' +
              esc(sfOpts[oi].label) +
              '</option>',
          )
        }
        sfLines.push(indent + '        </select></td>')
      } else if (sfCol.type === 'textarea') {
        sfLines.push(
          indent +
            '        <td class="fb-sf-td"><textarea name="' +
            fieldName +
            '" class="fb-input fb-sf-input" rows="2"' +
            reqAttr +
            phAttr +
            '></textarea></td>',
        )
      } else {
        var inputType =
          sfCol.type === 'number'
            ? 'number'
            : sfCol.type === 'email'
              ? 'email'
              : sfCol.type === 'date'
                ? 'date'
                : sfCol.type === 'url'
                  ? 'url'
                  : 'text'
        sfLines.push(
          indent +
            '        <td class="fb-sf-td"><input type="' +
            inputType +
            '" name="' +
            fieldName +
            '" class="fb-input fb-sf-input"' +
            reqAttr +
            phAttr +
            ' /></td>',
        )
      }
    }
    sfLines.push(
      indent +
        '        <td class="fb-sf-td fb-sf-actions"><button type="button" class="fb-sf-remove" onclick="this.closest(&quot;.fb-sf-row&quot;).remove();(function(t){var rows=t.querySelectorAll(&quot;.fb-sf-row&quot;);for(var i=0;i&lt;rows.length;i++)rows[i].querySelector(&quot;.fb-sf-num&quot;).textContent=i+1;})(this.closest(&quot;.fb-subform&quot;));" title="Remove row">✕</button></td>',
    )
    sfLines.push(indent + '      </tr>')
  }
  sfLines.push(indent + '    </tbody>')
  sfLines.push(indent + '  </table>')
  sfLines.push(
    indent +
      '  <button type="button" class="fb-sf-add" onclick="var sf=this.closest(&quot;.fb-subform&quot;);var tbody=sf.querySelector(&quot;.fb-sf-body&quot;);var max=parseInt(sf.dataset.sfMax)||100;if(tbody.children.length>=max){alert(&quot;Maximum &quot;+max+&quot; rows allowed&quot;);return;}var first=tbody.querySelector(&quot;.fb-sf-row&quot;);if(!first)return;var clone=first.cloneNode(true);var inputs=clone.querySelectorAll(&quot;input,select,textarea&quot;);for(var i=0;i&lt;inputs.length;i++){inputs[i].value=&quot;&quot;;var n=inputs[i].name;if(n)inputs[i].name=n.replace(/\\[\\d+\\]/,&quot;[&quot;+(tbody.children.length)+&quot;]&quot;);}clone.querySelector(&quot;.fb-sf-num&quot;).textContent=tbody.children.length+1;tbody.appendChild(clone);">+ Add Row</button>',
  )
  sfLines.push(indent + '</div>')
  return sfLines.join('\n')
}
